/**
 * @file LandingPage.jsx
 * @description Public homepage for SimpleCode. Shows value propositions, features, and direct CTA to onboard users.
 *              Sections: Hero → Features → CTA → Footer
 *              Design: shadcn-inspired light theme with emerald brand accent.
 *              All data constants are extracted to the top of the file.
 *              Sub-sections are split into focused render functions for readability.
 * @module components/LandingPage
 */
import React, { useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import Navbar from "./layout/Navbar";
import Footer from "./layout/Footer";
import Button from "./ui/Button";
import { FcGoogle } from "react-icons/fc";
import { FiArrowRight, FiBookOpen } from "react-icons/fi";

/* ══════════════════════════════════════════════════════════════
 * DATA CONSTANTS
 * ══════════════════════════════════════════════════════════════ */

/**
 * Platform features displayed in the list/grid format.
 * Designed to cleanly swap between mobile bullet-lists and desktop card grids.
 */
const FEATURES = [
  {
    title: "Algorithm Mastery",
    description: "Master data structures and algorithms with progressive difficulty levels and real-time coding practice.",
  },
  {
    title: "System Design Excellence",
    description: "Learn to architect scalable systems with real-world case studies and hands-on design challenges.",
  },
  {
    title: "Instant Feedback",
    description: "Get immediate results with detailed analysis, performance metrics, and personalized improvement tips.",
  },
  {
    title: "Guided Learning Path",
    description: "Follow structured learning journeys tailored to your skill level and career aspirations.",
  },
  {
    title: "Progress Analytics",
    description: "Track your growth with comprehensive analytics, skill assessments, and achievement milestones.",
  },
  {
    title: "Industry Patterns",
    description: "Learn proven design patterns, database optimization, and distributed systems fundamentals.",
  },
];

/* ══════════════════════════════════════════════════════════════
 * SUB-COMPONENTS
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component HeroSection
 * @description Displays the primary value proposition, dual CTAs (conditional on auth state),
 *              and the decorative code preview block on desktop breakpoints.
 *
 * @param {Object} props
 * @param {Function} props.navigate - Router navigation function.
 * @param {boolean} props.isAuthenticated - Determines if user is logged in.
 * @param {Function} props.signInWithGoogle - Authentication handler.
 * 
 * @returns {JSX.Element} The rendered Hero section
 */
const HeroSection = ({ navigate, isAuthenticated, signInWithGoogle }) => (
  <section
    id="home"
    className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 px-8 sm:px-12 lg:px-16 bg-muted overflow-hidden"
  >
    {/* Subtle grid pattern background */}
    <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808018_1px,transparent_2px),linear-gradient(to_bottom,#80808018_1px,transparent_2px)] bg-[size:24px_24px] pointer-events-none" />
    <div className="relative z-10 max-w-8xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-32 items-center">
        {/* ── Left: Copy ── */}
        <div className="space-y-8 text-left">
          <div className="space-y-4">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.1]">
              Accelerate Your{" "}
              <span className="bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent">
                Tech Journey
              </span>{" "}
              to Success
            </h1>

            <p className="text-md md:text-lg text-muted-foreground leading-relaxed max-w-lg">
              Practice <strong className="text-foreground">DSA Problems</strong>,
              build{" "}
              <strong className="text-foreground">
                System Design Expertise
              </strong>
              , and level up for your dream tech role — all in one platform.
            </p>
          </div>

          {/* ── CTAs ── */}
          <div className="flex flex-col sm:flex-row gap-3">
            {isAuthenticated ? (
              <Button size="lg" onClick={() => navigate("/dashboard")}>
                <FiBookOpen className="w-5 h-5" />
                Start Learning
                <FiArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <>
                <Button size="lg" onClick={() => navigate("/learning")}>
                  <FiBookOpen className="w-5 h-5" />
                  Start Learning
                  <FiArrowRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={signInWithGoogle}
                >
                  <FcGoogle className="w-5 h-5" />
                  Sign in with Google
                </Button>
              </>
            )}
          </div>
        </div>

        {/* ── Right: Code Preview ── */}
        <div className="hidden lg:block">
          <CodePreview />
        </div>
      </div>
    </div>
  </section>
);

/**
 * @component CodePreview
 * @description Purely decorative component rendering a mock javascript snippet.
 *              Embedded inside a glass-like shadow container for the Hero section.
 * 
 * @returns {JSX.Element} The rendered Code Preview block
 */
const CodePreview = () => (
  <div className="relative">
    {/* Glow background */}
    <div className="absolute -inset-4" />

    <div className="relative bg-slate-950 rounded border-2 border-slate-800 overflow-hidden shadow-2xl text-left">
      {/* Title bar */}
      <div className="flex items-center gap-2 px-4 py-3 bg-slate-900 border-b border-slate-800">
        <div className="w-3 h-3 bg-red-500 rounded-full" />
        <div className="w-3 h-3 bg-yellow-500 rounded-full" />
        <div className="w-3 h-3 bg-green-500 rounded-full" />
        <span className="text-accent text-xs ml-2 font-mono">
          success.js
        </span>
      </div>

      {/* Code body */}
      <div className="px-5 py-4 font-mono text-sm leading-relaxed text-slate-700 overflow-x-auto bg-accent">
        <span className="text-blue-700">class</span>{" "}
        <span className="text-yellow-700 font-semibold">TechJourney</span>{" "}
        <span className="text-slate-500">{"{"}</span>
        <br />

        <span className="ml-4 text-blue-700">constructor</span>
        <span className="text-slate-500">() {"{"}</span>
        <br />
        <span className="ml-8 text-slate-500 italic">
          {"// Your journey starts here"}
        </span>
        <br />
        <span className="ml-4 text-slate-500">{"}"}</span>
        <br />
        <br />

        <span className="ml-4 text-blue-700">async</span>{" "}
        <span className="text-emerald-700 font-medium">masterDSA</span>
        <span className="text-slate-500">() {"{"}</span>
        <br />
        <span className="ml-8 text-purple-700">return</span>{" "}
        <span className="text-orange-700">'Problem Solving Skills'</span>
        <span className="text-slate-500">;</span>
        <br />
        <span className="ml-4 text-slate-500">{"}"}</span>
        <br />
        <br />

        <span className="ml-4 text-blue-700">async</span>{" "}
        <span className="text-emerald-700 font-medium">designSystems</span>
        <span className="text-slate-500">() {"{"}</span>
        <br />
        <span className="ml-8 text-purple-700">return</span>{" "}
        <span className="text-orange-700">'Architecture Excellence'</span>
        <span className="text-slate-500">;</span>
        <br />
        <span className="ml-4 text-slate-500">{"}"}</span>
        <br />
        <br />

        <span className="ml-4 text-blue-700">async</span>{" "}
        <span className="text-emerald-700 font-medium">achieveSuccess</span>
        <span className="text-slate-500">() {"{"}</span>
        <br />
        <span className="ml-8 text-blue-700">const</span>{" "}
        <span className="text-sky-700">skills</span>{" "}
        <span className="text-slate-500">=</span>{" "}
        <span className="text-purple-700">await</span>{" "}
        <span className="text-yellow-700">Promise</span>
        <span className="text-slate-500">.</span>
        <span className="text-emerald-700">all</span>
        <span className="text-slate-500">([</span>
        <br />
        <span className="ml-12 text-blue-700">this</span>
        <span className="text-slate-500">.</span>
        <span className="text-emerald-700">masterDSA</span>
        <span className="text-slate-500">(),</span>
        <br />
        <span className="ml-12 text-blue-700">this</span>
        <span className="text-slate-500">.</span>
        <span className="text-emerald-700">designSystems</span>
        <span className="text-slate-500">()</span>
        <br />
        <span className="ml-8 text-slate-500">]);</span>
        <br />
        <span className="ml-8 text-purple-700">return</span>{" "}
        <span className="text-orange-700">'Dream Job Unlocked! 🚀'</span>
        <span className="text-slate-500">;</span>
        <br />
        <span className="ml-4 text-slate-500">{"}"}</span>
        <br />
        <span className="text-slate-500">{"};"}</span>
      </div>
    </div>
  </div>
);

/**
 * @component FeaturesSection
 * @description Renders the platform value propositions. Responsive behavior converts
 *              the grid of cards on desktop to a cleanly grouped bullet list on mobile.
 * 
 * @returns {JSX.Element} The rendered Features section
 */
const FeaturesSection = () => (
  <section id="features" className="py-16 sm:py-24 px-8 sm:px-12 lg:px-16 bg-accent border-y-4 border-emerald-600">
    <div className="max-w-8xl mx-auto">
      {/* Section header */}
      <div className="text-left mb-12 max-w-2xl">
        <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
          Your Complete{" "}
          <span className="bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent">
            Learning Ecosystem
          </span>
        </h2>
        <p className="text-md md:text-lg text-muted-foreground leading-relaxed">
          From junior developer to senior architect — everything you need to
          excel in your tech career journey.
        </p>
      </div>

      {/* Cards/List flexbox */}
      <div className="flex flex-col sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {FEATURES.map((feature) => (
          <div
            key={feature.title}
            className="group flex sm:block items-start sm:p-6 sm:rounded sm:border-l-4 sm:border-emerald-600 sm:bg-card transition-all duration-300"
          >
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 mt-2 mr-3 sm:hidden shrink-0" />
            <div>
              <h3 className="text-base font-medium sm:font-semibold text-foreground sm:text-emerald-600 sm:mb-2 transition-colors duration-200">
                {feature.title}
              </h3>
              <p className="hidden sm:block text-sm text-muted-foreground leading-relaxed">
                {feature.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/**
 * @component CTASection
 * @description Bottom-of-page contextual trigger. Encourages unauthenticated users to sign up
 *              or authenticated users to proceed to their dashboard.
 *
 * @param {Object} props
 * @param {Function} props.navigate - Router navigation function.
 * @param {boolean} props.isAuthenticated - Used to toggle the CTA action.
 * @param {Function} props.signInWithGoogle - Authentication handler.
 * 
 * @returns {JSX.Element} The rendered CTA section
 */
const CTASection = ({ navigate, isAuthenticated, signInWithGoogle }) => (
  <section
    id="cta"
    className="relative py-16 sm:py-24 px-8 sm:px-12 lg:px-16 bg-muted overflow-hidden"
  >
    {/* Subtle grid pattern background */}
    <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808018_1px,transparent_2px),linear-gradient(to_bottom,#80808018_1px,transparent_2px)] bg-[size:24px_24px] pointer-events-none" />
    <div className="relative max-w-3xl mx-auto text-center">
      <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
        Ready to Transform{" "}
        <span className="bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent">
          Your Tech Career?
        </span>
      </h2>
      <p className="text-md md:text-lg text-muted-foreground mb-8 max-w-xl mx-auto leading-relaxed">
        Join thousands of developers who are already accelerating their careers
        with our comprehensive learning platform.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {isAuthenticated ? (
          <Button size="lg" onClick={() => navigate("/dashboard")}>
            Go to Dashboard
            <FiArrowRight className="w-4 h-4" />
          </Button>
        ) : (
          <>
            <Button size="lg" onClick={() => navigate("/learning")}>
              Begin Your Journey
              <FiArrowRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="lg" onClick={signInWithGoogle}>
              <FcGoogle className="w-5 h-5" />
              Sign in with Google
            </Button>
          </>
        )}
      </div>
    </div>
  </section>
);

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component LandingPage
 * @description The main layout wrapper for the public landing page.
 *              Integrates the global Navbar, Footer, and semantic section components.
 * 
 * @param {Object} props
 * @param {Function} props.navigate - Reach Router navigation function
 * 
 * @returns {JSX.Element} The fully assembled Landing Page view
 */

const LandingPage = ({ navigate }) => {
  const { isAuthenticated, signInWithGoogle } = useAuth();

  // Reset browser tab title to the default when landing page mounts
  useEffect(() => { document.title = 'SimpleCode \u2014 Accelerate Your Tech Journey'; }, []);

  return (
    <div className="min-h-screen text-foreground overflow-x-hidden">
      <Navbar navigate={navigate} />

      <main>
        <HeroSection
          navigate={navigate}
          isAuthenticated={isAuthenticated}
          signInWithGoogle={signInWithGoogle}
        />
        <FeaturesSection />
        <CTASection
          navigate={navigate}
          isAuthenticated={isAuthenticated}
          signInWithGoogle={signInWithGoogle}
        />
      </main>

      <Footer navigate={navigate} />
    </div>
  );
};

export default LandingPage;